import React from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { FormSection } from '../../form/FormSection';
import { ResponsiveInput } from '../../form/ResponsiveInput';
import { FORM_STYLES } from '../../../styles/formStyles';
import { FormValues } from '../CreateStep1V3';
import dayjs, { Dayjs } from 'dayjs';

interface ContractPeriodSectionProps {
  values: FormValues;
  handleChange: (name: string) => (value: any) => void;
  rentalPeriodStart?: string; // Från DB (godkänd uthyrningsperiod)
  rentalPeriodEnd?: string;   // Från DB (godkänd uthyrningsperiod)
  loading?: boolean;
}

export const ContractPeriodSection: React.FC<ContractPeriodSectionProps> = ({
  values,
  handleChange,
  rentalPeriodStart,
  rentalPeriodEnd,
  loading = false
}) => {
  // Validera att avtalsperioden är inom uthyrningsperioden
  const validateContractPeriod = (): { isValid: boolean; message?: string } => {
    if (!rentalPeriodStart || !rentalPeriodEnd) {
      return { isValid: false, message: 'Laddar uthyrningsperiod...' };
    }

    if (!values.contractStartDate || !values.contractEndDate) {
      return { isValid: false, message: 'Ange både start- och slutdatum för avtalet' };
    }

    const contractStart = dayjs(values.contractStartDate);
    const contractEnd = dayjs(values.contractEndDate);
    const rentalStart = dayjs(rentalPeriodStart);
    const rentalEnd = dayjs(rentalPeriodEnd);

    // Kontrollera att startdatum är före slutdatum
    if (contractStart.isAfter(contractEnd)) {
      return { isValid: false, message: 'Startdatum måste vara före slutdatum' };
    }

    // Kontrollera att avtalsperioden är inom uthyrningsperioden
    if (contractStart.isBefore(rentalStart)) {
      return { 
        isValid: false, 
        message: `Avtalet kan inte starta före ${rentalStart.format('YYYY-MM-DD')} (godkänd uthyrningsperiod)` 
      };
    }

    if (contractEnd.isAfter(rentalEnd)) {
      return { 
        isValid: false, 
        message: `Avtalet kan inte sluta efter ${rentalEnd.format('YYYY-MM-DD')} (godkänd uthyrningsperiod)` 
      };
    }

    // Beräkna avtalsperiod i månader
    const contractMonths = contractEnd.diff(contractStart, 'month', true);
    const totalMonths = rentalEnd.diff(rentalStart, 'month', true);

    return {
      isValid: true,
      message: `✅ Avtalsperioden är giltig (${Math.round(contractMonths)} av ${Math.round(totalMonths)} månader)`
    };
  };

  const validation = validateContractPeriod();

  return (
    <FormSection
      icon={CalendarToday}
      iconColor="#62B8E9"
      title="Avtalsperiod"
    >
      {/* Information om godkänd uthyrningsperiod */}
      {loading ? (
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
          <Typography sx={{ fontSize: '14px', color: '#6b7280' }}>
            📋 Laddar godkänd uthyrningsperiod...
          </Typography>
        </Box>
      ) : rentalPeriodStart && rentalPeriodEnd ? (
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f0f9ff', borderRadius: 1, border: '1px solid #bae6fd' }}>
          <Typography sx={{ fontSize: '14px', color: '#0369a1', fontWeight: 500 }}>
            📋 BRF-godkänd uthyrningsperiod: {dayjs(rentalPeriodStart).format('YYYY-MM-DD')} → {dayjs(rentalPeriodEnd).format('YYYY-MM-DD')}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#0369a1', mt: 0.5 }}>
            Avtalsperioden måste vara inom denna period
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#fef3c7', borderRadius: 1, border: '1px solid #fbbf24' }}>
          <Typography sx={{ fontSize: '14px', color: '#92400e' }}>
            ⚠️ Kunde inte hämta uthyrningsperiod från ansökan
          </Typography>
        </Box>
      )}

      {/* Avtalsperiod input */}
      <Box sx={FORM_STYLES.layout.twoColumn}>
        <ResponsiveInput
          label="Avtal från datum"
          value={values.contractStartDate}
          onChange={handleChange('contractStartDate')}
          type="date"
          infoText="När ska hyresavtalet börja gälla? Måste vara inom den BRF-godkända perioden."
        />

        <ResponsiveInput
          label="Avtal till datum"
          value={values.contractEndDate}
          onChange={handleChange('contractEndDate')}
          type="date"
          infoText="När ska hyresavtalet sluta? Måste vara inom den BRF-godkända perioden."
        />
      </Box>

      {/* Validering och feedback */}
      {values.contractStartDate && values.contractEndDate && (
        <Box sx={{ mt: 3 }}>
          <Alert 
            severity={validation.isValid ? 'success' : 'error'}
            sx={{ fontSize: '14px' }}
          >
            {validation.message}
          </Alert>
        </Box>
      )}

      {/* Hjälptext */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f9fafb', borderRadius: 1 }}>
        <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
          💡 <strong>Tips:</strong> Avtalsperioden kan vara kortare än den BRF-godkända uthyrningsperioden. 
          Till exempel kan du ha fått godkänt för 6 månader men bara vilja hyra ut i 3 månader.
        </Typography>
      </Box>
    </FormSection>
  );
};

